import { DatagramAssembler, encodeFrame, FrameDecoder } from "./wire";
import { inputPackets, INPUT_HISTORY_LIMIT } from "./input-packets";
import type { PlayerInput } from "./types";
export interface TransportEndpoint {
  url: string;
  certificateHashes?: { algorithm: string; value: number[] }[];
}
interface BrowserTransport {
  ready: Promise<void>;
  closed: Promise<unknown>;
  close(): void;
  datagrams: {
    readable: ReadableStream<Uint8Array>;
    writable: WritableStream<Uint8Array>;
    maxDatagramSize: number;
    outgoingHighWaterMark?: number;
    incomingHighWaterMark?: number;
    outgoingMaxBufferedDatagrams?: number;
    incomingMaxBufferedDatagrams?: number;
    outgoingMaxAge?: number;
    incomingMaxAge?: number;
  };
  incomingUnidirectionalStreams: ReadableStream<ReadableStream<Uint8Array>>;
  createBidirectionalStream(): Promise<{
    readable: ReadableStream<Uint8Array>;
    writable: WritableStream<Uint8Array>;
  }>;
}
type Constructor = new (
  url: string,
  options?: {
    serverCertificateHashes?: { algorithm: string; value: Uint8Array }[];
  },
) => BrowserTransport;
const encoder = new TextEncoder();
/** An authoritative denial is not a failed transport probe to retry over WS. */
export class QuicHandshakeRejection extends Error {
  constructor(
    readonly frame: ArrayBuffer,
    message: string,
  ) {
    super(message);
    this.name = "QuicHandshakeRejection";
  }
}
/** This exact startup probe owned the session before its datagram proof failed. */
export class QuicOwnedProbeFailure extends Error {
  constructor(message: string) {
    super(message);
    this.name = "QuicOwnedProbeFailure";
  }
}
export class QuicTransport {
  private transport: BrowserTransport;
  private control?: WritableStreamDefaultWriter<Uint8Array>;
  private datagrams?: WritableStreamDefaultWriter<Uint8Array>;
  private stopped = false;
  private active = false;
  private queued: Uint8Array[] = [];
  private controlWrites: Promise<void> = Promise.resolve();
  private datagramWriting = false;
  private pendingDatagrams: Uint8Array[] = [];
  private inputHistory: PlayerInput[] = [];
  private matchId?: string;
  private controlPending = 0;
  private welcome?: () => void;
  private receivedWelcome = false;
  private rejectWelcome?: (error: Error) => void;
  private probe?: () => void;
  private probeNonce = Date.now();
  private onFrame: (data: ArrayBuffer) => void;
  private onClose: () => void;
  private constructor(
    endpoint: TransportEndpoint,
    onFrame: (data: ArrayBuffer) => void,
    onClose: () => void,
  ) {
    const WT = (globalThis as unknown as { WebTransport: Constructor })
      .WebTransport;
    this.transport = new WT(endpoint.url, {
      serverCertificateHashes: endpoint.certificateHashes?.map((hash) => ({
        algorithm: hash.algorithm,
        value: new Uint8Array(hash.value),
      })),
    });
    this.onFrame = onFrame;
    this.onClose = onClose;
    this.transport.closed.then(
      () => this.closed(),
      () => this.closed(),
    );
  }
  static available(): boolean {
    return (
      typeof (globalThis as { WebTransport?: unknown }).WebTransport ===
      "function"
    );
  }
  static async open(
    endpoint: TransportEndpoint,
    hello: unknown,
    onFrame: (data: ArrayBuffer) => void,
    onClose: () => void,
  ): Promise<QuicTransport> {
    const session = new QuicTransport(endpoint, onFrame, onClose);
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      await Promise.race([
        session.start(hello),
        new Promise<never>((_, reject) => {
          timer = setTimeout(
            () => reject(new Error("QUIC probe timed out")),
            2500,
          );
        }),
      ]);
      return session;
    } catch (error) {
      session.close();
      if (session.receivedWelcome && !(error instanceof QuicHandshakeRejection))
        throw new QuicOwnedProbeFailure(
          error instanceof Error ? error.message : "QUIC probe failed",
        );
      throw error;
    } finally {
      clearTimeout(timer);
    }
  }
  private async start(hello: unknown): Promise<void> {
    await this.transport.ready;
    const limits = {
      outgoingHighWaterMark: 1,
      outgoingMaxBufferedDatagrams: 1,
      outgoingMaxAge: 100,
      incomingMaxAge: 250,
      incomingHighWaterMark: 64,
      incomingMaxBufferedDatagrams: 64,
    };
    for (const [name, value] of Object.entries(limits)) {
      const datagrams = this.transport.datagrams as unknown as Record<
        string,
        unknown
      >;
      if (name in datagrams)
        try {
          datagrams[name] = value;
        } catch {
          /* Older implementations keep browser defaults. */
        }
    }
    if (this.stopped) throw new Error("QUIC connection cancelled");
    const stream = await this.transport.createBidirectionalStream();
    this.control = stream.writable.getWriter();
    this.datagrams = this.transport.datagrams.writable.getWriter();
    const welcomed = new Promise<void>((resolve, reject) => {
      this.welcome = resolve;
      this.rejectWelcome = reject;
    });
    // A fast rejection can arrive while the hello writer is still flushing.
    // Keep it observed until start() reaches the awaited handshake promise.
    welcomed.catch(() => {});
    this.readControl(stream.readable, true).catch(() => this.closed());
    this.readDatagrams().catch(() => this.closed());
    this.readIncoming().catch(() => this.closed());
    await this.control.write(
      encodeFrame(encoder.encode(JSON.stringify(hello))),
    );
    await welcomed;
    this.welcome = undefined;
    this.rejectWelcome = undefined;
    const proven = new Promise<void>((resolve) => {
      this.probe = resolve;
    });
    const ping = encoder.encode(
      JSON.stringify({ type: "ping", nonce: this.probeNonce }),
    );
    await this.datagrams.write(ping);
    const retry = setInterval(() => {
      this.datagrams?.write(ping).catch(() => {});
    }, 350);
    let probeTimer: ReturnType<typeof setTimeout> | undefined;
    try {
      await Promise.race([
        proven,
        new Promise<never>((_, reject) => {
          probeTimer = setTimeout(
            () => reject(new Error("QUIC datagrams are unavailable")),
            1500,
          );
        }),
      ]);
    } finally {
      clearInterval(retry);
      clearTimeout(probeTimer);
    }
  }
  activate(): void {
    if (this.stopped) return;
    this.active = true;
    for (const packet of this.queued) this.deliver(packet);
    this.queued = [];
  }
  private closed(): void {
    if (this.stopped) return;
    const notify = this.active;
    this.stopped = true;
    this.transport.close();
    if (notify) this.onClose();
  }
  private deliver(packet: Uint8Array): void {
    if (this.stopped) return;
    if (!this.active) {
      // Handshake messages are always uncompressed; snapshots stay buffered until transport proof.
      try {
        const data = JSON.parse(new TextDecoder().decode(packet));
        if (data.type === "welcome") {
          this.receivedWelcome = true;
          this.welcome?.();
        }
        if (
          data.type === "error" &&
          ["session_in_use", "client_update_required"].includes(data.code) &&
          this.rejectWelcome
        ) {
          this.rejectWelcome(
            new QuicHandshakeRejection(
              packet.slice().buffer,
              String(data.message),
            ),
          );
          return;
        }
      } catch {
        /* gzip payload */
      }
      if (this.queued.length < 32) this.queued.push(packet);
      return;
    }
    this.onFrame(packet.slice().buffer);
  }
  private async readControl(
    stream: ReadableStream<Uint8Array>,
    persistent = false,
  ): Promise<void> {
    const reader = stream.getReader(),
      decoder = new FrameDecoder();
    try {
      while (!this.stopped) {
        const { value, done } = await reader.read();
        if (done) break;
        for (const frame of decoder.push(value)) this.deliver(frame);
      }
    } finally {
      reader.releaseLock();
      if (persistent) this.closed();
    }
  }
  private async readIncoming(): Promise<void> {
    const reader = this.transport.incomingUnidirectionalStreams.getReader();
    try {
      while (!this.stopped) {
        const { value, done } = await reader.read();
        if (done) break;
        this.readControl(value).catch(() => this.closed());
      }
    } finally {
      reader.releaseLock();
    }
  }
  private async readDatagrams(): Promise<void> {
    const reader = this.transport.datagrams.readable.getReader(),
      assembler = new DatagramAssembler();
    try {
      while (!this.stopped) {
        const { value, done } = await reader.read();
        if (done) break;
        if (value[0] === 0x57 && value[1] === 0x5a) {
          const packet = assembler.push(value);
          if (packet) this.deliver(packet);
        } else {
          try {
            const message = JSON.parse(new TextDecoder().decode(value));
            if (message.type === "pong" && message.nonce === this.probeNonce)
              this.probe?.();
            else this.deliver(value);
          } catch {
            /* Invalid unreliable datagram. */
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  }
  setMatch(id: string): void {
    if (id !== this.matchId) {
      this.matchId = id;
      this.inputHistory = [];
      this.pendingDatagrams = [];
    }
  }
  sendInputs(inputs: PlayerInput[]): boolean {
    if (!this.active || this.stopped || !this.matchId) return false;
    if (!inputs.length) return true;
    this.inputHistory.push(...inputs);
    this.inputHistory = this.inputHistory.slice(-INPUT_HISTORY_LIMIT);
    try {
      // A dropped packet is recovered from subsequent history. Split at the
      // path MTU instead of deleting older commands from a catch-up frame.
      this.pendingDatagrams = inputPackets(
        this.inputHistory,
        this.transport.datagrams.maxDatagramSize,
        this.matchId,
      );
    } catch {
      return false;
    }
    this.flushDatagram();
    for (const input of inputs) {
      // One-shot actions also use the reliable stream. The authority dedupes
      // their original sequence and never applies an already consumed tick.
      if (input.cast.some(Boolean))
        this.sendControl({ type: "input", matchId: this.matchId, input });
    }
    return true;
  }
  send(value: any): boolean {
    if (!this.active || this.stopped) return false;
    if (
      (value?.type === "input" || value?.type === "input_bundle") &&
      value.matchId !== undefined &&
      value.matchId !== this.matchId
    )
      return false;
    if (value?.type === "ping") {
      // Timing probes share the actual datagram path but never queue behind input.
      if (
        this.datagramWriting ||
        this.pendingDatagrams.length ||
        !this.datagrams ||
        (this.datagrams.desiredSize ?? 0) <= 0
      )
        return false;
      this.pendingDatagrams = [encoder.encode(JSON.stringify(value))];
      this.flushDatagram();
      return true;
    }
    if (value?.type === "input") return this.sendInputs([value.input]);
    if (value?.type === "input_bundle") return this.sendInputs(value.inputs);
    if (
      value?.type === "leave" ||
      value?.type === "matchmake" ||
      value?.type === "create_room" ||
      value?.type === "join_room"
    ) {
      this.inputHistory = [];
      this.pendingDatagrams = [];
      this.matchId = undefined;
    }
    return this.sendControl(value);
  }
  private sendControl(value: unknown): boolean {
    if (this.controlPending >= 64) return false;
    const packet = encodeFrame(
      encoder.encode(typeof value === "string" ? value : JSON.stringify(value)),
    );
    this.controlPending++;
    this.controlWrites = this.controlWrites
      .then(() => this.control!.write(packet))
      .catch(() => this.closed())
      .finally(() => {
        this.controlPending--;
      });
    return true;
  }
  private flushDatagram(): void {
    if (this.datagramWriting || !this.pendingDatagrams.length || this.stopped)
      return;
    const packet = this.pendingDatagrams.shift()!;
    this.datagramWriting = true;
    this.datagrams!.write(packet)
      .catch(() => this.closed())
      .finally(() => {
        this.datagramWriting = false;
        this.flushDatagram();
      });
  }
  close(): void {
    this.stopped = true;
    this.active = false;
    this.transport.close();
  }
}
